import { Component, Inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import Swal from '../../../shared/utils/swal.util';
import { OpcaoPerfil, PerfilUsuario, PerfilUsuarioService } from './perfil-usuario.service';
import { PerfilCropComponent, PerfilCropResult } from './perfil-crop/perfil-crop.component';

interface DialogDataMock<T> {
  data: T;
  action: string;
}

@Component({
  selector: 'app-meu-perfil',
  standalone: false,
  templateUrl: './meu-perfil.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meu-perfil.component.css'
})
export class MeuPerfilComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly perfilUsuarioService = inject(PerfilUsuarioService);

  usuarioForm!: UntypedFormGroup;
  hide = true;
  previewImage: string | null = null;
  midiaBase64: string | null = null;
  perfis: OpcaoPerfil[] = this.perfilUsuarioService.perfis;
  cargos: OpcaoPerfil[] = this.perfilUsuarioService.cargos;
  sexos: OpcaoPerfil[] = this.perfilUsuarioService.sexos;
  racas: OpcaoPerfil[] = this.perfilUsuarioService.racas;
  necessidadeEspecial: OpcaoPerfil[] = this.perfilUsuarioService.necessidadesEspeciais;
  usuario!: PerfilUsuario;
  perfilNome = '';
  defaultImage = 'assets/images/profile.png';

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<MeuPerfilComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: DialogDataMock<any>,
  ) { }

  get f() {
    return this.usuarioForm.controls;
  }

  get nomeExibicao(): string {
    return this.perfilUsuarioService.nomeExibicao(this.usuario);
  }

  ngOnInit(): void {
    this.createEmptyForm();
    this.carregarPerfilAtual();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const arquivo = input.files?.[0] ?? null;

    input.value = '';

    if (!arquivo) {
      return;
    }

    if (!arquivo.type.startsWith('image/')) {
      Swal.fire({
        icon: 'warning',
        title: 'Formato não aceito',
        text: 'A foto do perfil deve ser um arquivo de imagem.'
      });
      return;
    }

    this.abrirRecorte(arquivo);
  }

  removerFoto(): void {
    this.previewImage = null;
    this.midiaBase64 = null;
    this.usuarioForm.patchValue({ midia: null });
    this.changeDetectorRef.detectChanges();
  }

  onSubmit(): void {
    if (this.usuarioForm.invalid) {
      this.usuarioForm.markAllAsTouched();
      return;
    }

    this.perfilUsuarioService.atualizar({
      ...this.usuarioForm.value,
      midia: this.midiaBase64
    });

    this.dialogRef.close({
      success: true,
      message: 'Perfil atualizado com sucesso.',
      data: this.perfilUsuarioService.perfil
    });
  }

  private createEmptyForm(): void {
    this.usuarioForm = this.formBuilder.group({
      nomeSocial: [null],
      telefone: [null],
      sexoId: [null],
      racaId: [null],
      nacionalidade: [null],
      naturalidade: [null],
      setor: [null],
      cargoId: [null],
      dataNascimento: [null, [Validators.required, this.dateOfBirthValidator]],
      midia: [null],
      necessidadeEspeciaisIds: [[]]
    });
  }

  private carregarPerfilAtual(): void {
    this.usuario = this.perfilUsuarioService.perfil;
    this.perfilNome = this.usuario.perfil;
    this.previewImage = this.usuario.midia;
    this.midiaBase64 = this.usuario.midia;

    const dataNascimento = this.usuario.dataNascimento
      ? this.usuario.dataNascimento.substring(0, 10)
      : null;

    this.usuarioForm.patchValue({
      nomeSocial: this.usuario.nomeSocial,
      telefone: this.usuario.telefone,
      sexoId: this.usuario.sexoId,
      racaId: this.usuario.racaId,
      nacionalidade: this.usuario.nacionalidade,
      naturalidade: this.usuario.naturalidade,
      setor: this.usuario.setor,
      cargoId: this.usuario.cargoId,
      dataNascimento,
      midia: this.usuario.midia,
      necessidadeEspeciaisIds: [...this.usuario.necessidadeEspeciaisIds]
    });
  }

  private abrirRecorte(arquivo: File): void {
    const cropRef = this.dialog.open(PerfilCropComponent, {
      data: { arquivo },
      panelClass: 'dialog-rounded',
      width: '640px',
      maxWidth: '95vw'
    });

    cropRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resultado: PerfilCropResult | undefined) => {
        if (!resultado) {
          return;
        }

        this.previewImage = resultado.base64;
        this.midiaBase64 = resultado.base64;
        this.usuarioForm.patchValue({ midia: resultado.base64 });
        this.changeDetectorRef.detectChanges();
      });
  }

  private dateOfBirthValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const date = new Date(control.value);
    const today = new Date();

    if (Number.isNaN(date.getTime()) || date > today) {
      return { dateOfBirth: true };
    }

    return null;
  }
}
